import {
    checkHql,
    type HqlCatalog,
    type HqlCatalogEquipmentType,
    type HqlCatalogInfoType,
    type HqlFieldRead,
    hqlFieldsRead,
} from '@rock/hql';
import type { AppliesToOwner, SpaceId } from '@rock/types';

export type BrokenQuery = { type: AppliesToOwner; errors: string[] };

/**
 * The approved queries (consideration and lesson) a catalog change would break: valid against the
 * catalog as it is, invalid against the proposed one. Approved types only, as on the dashboard. A query
 * that's already broken doesn't block an unrelated edit; it is already frozen and already red there.
 */
export function queriesBrokenBy(types: AppliesToOwner[], current: HqlCatalog, proposed: HqlCatalog): BrokenQuery[] {
    return approvedValidQueries(types, current).flatMap(({ type, query }) => {
        const { errors } = checkHql(query, proposed, { spaceId: type.spaceId });
        return errors.length > 0 ? [{ type, errors: errors.map((error) => error.message) }] : [];
    });
}

export type ChangedReading = { type: AppliesToOwner; answers: string[] };

/**
 * The approved queries that read a select field the change takes an option value from. They still
 * compile, but every stored answer holding that value now reads as unknown, which flips `IS UNKNOWN` and
 * `!=`. Only a warning: the answers may already be moved, or the option never chosen.
 */
export function readingsChangedBy(
    types: AppliesToOwner[],
    current: HqlCatalog,
    proposed: HqlCatalog,
): ChangedReading[] {
    return approvedValidQueries(types, current).flatMap(({ type, compiled }) => {
        const answers = hqlFieldsRead(compiled).flatMap((read) => removedOptionAnswers(read, current, proposed));
        return answers.length > 0 ? [{ type, answers }] : [];
    });
}

/** The catalog with one equipment type replaced (a publish), added, or removed (`undefined`). */
export function withEquipmentType(
    catalog: HqlCatalog,
    typeId: string,
    replacement: HqlCatalogEquipmentType | undefined,
): HqlCatalog {
    const others = catalog.equipmentTypes.filter((type) => type.id !== typeId);
    return { ...catalog, equipmentTypes: replacement ? [...others, replacement] : others };
}

/** The catalog with one info type replaced, added, or removed (`undefined`). */
export function withInfoType(
    catalog: HqlCatalog,
    typeId: string,
    replacement: HqlCatalogInfoType | undefined,
): HqlCatalog {
    const others = catalog.infoTypes.filter((type) => type.id !== typeId);
    return { ...catalog, infoTypes: replacement ? [...others, replacement] : others };
}

/** The catalog without one published space (unpublishing it). */
export function withoutSpace(catalog: HqlCatalog, spaceId: SpaceId): HqlCatalog {
    return { ...catalog, spaceIds: catalog.spaceIds.filter((id) => id !== spaceId) };
}

export function describeBrokenQueries(change: string, broken: BrokenQuery[]): string {
    const lines = broken.map(({ type, errors }) => `${type.title}: ${errors.join('; ')}`);
    const count = broken.length === 1 ? 'an approved query' : `${broken.length} approved queries`;
    return `${change} would break ${count}. Fix ${broken.length === 1 ? 'it' : 'them'} first. ${lines.join(' · ')}`;
}

export function describeChangedReadings(change: string, changed: ChangedReading[]): string {
    const lines = changed.map(({ type, answers }) => `${type.title}: ${answers.join('; ')}`);
    const count = changed.length === 1 ? 'an approved query reads' : `${changed.length} approved queries read`;
    return `${change} changes what ${count}. ${lines.join(' · ')}`;
}

function approvedValidQueries(types: AppliesToOwner[], catalog: HqlCatalog) {
    return types.flatMap((type) => {
        if (type.documentStatus !== 'approved' || !type.appliesTo) return [];
        const compiled = checkHql(type.appliesTo, catalog, { spaceId: type.spaceId }).compiled;
        return compiled ? [{ type, query: type.appliesTo, compiled }] : [];
    });
}

/** "answers of X to field will read as unknown", for each option value `proposed` takes from the field. */
function removedOptionAnswers(read: HqlFieldRead, current: HqlCatalog, proposed: HqlCatalog): string[] {
    const after = optionValues(proposed, read);
    if (!after) return [];
    const kept = new Set(after.map((value) => value.toLowerCase()));
    const field = read.source === 'info' ? `info type ${read.key}` : `${read.typeId}'s ${read.key}`;
    return (optionValues(current, read) ?? [])
        .filter((value) => !kept.has(value.toLowerCase()))
        .map((value) => `answers of "${value}" to ${field} will read as unknown`);
}

function optionValues(catalog: HqlCatalog, read: HqlFieldRead): string[] | undefined {
    const field =
        read.source === 'info'
            ? catalog.infoTypes.find((type) => type.id === read.key)
            : catalog.equipmentTypes
                  .find((type) => type.id === read.typeId)
                  ?.inputFields?.find((input) => input.dataKey === read.key);
    return field?.dataType === 'select' ? field.options?.map((option) => option.value) : undefined;
}
